import React, { useState, useEffect } from 'react';
import { 
  FiPlus, FiFilter, FiFileText, FiSend, FiCheckCircle
} from 'react-icons/fi';
import { DataService } from '../../Services/dataService';

export const SupportTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [viewPerspective] = useState('staff'); // Locked to staff for Admin Panel
  const [statusFilter, setStatusFilter] = useState('All');
  const [showResolveBox, setShowResolveBox] = useState(false);
  const [resolveReason, setResolveReason] = useState('');
  
  const [replyText, setReplyText] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchTickets();
  }, [statusFilter]);

  useEffect(() => {
    // Reset resolve box when ticket changes
    setShowResolveBox(false);
    setResolveReason('');
  }, [selectedTicketId]);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const res = await DataService.get('/support/list');
      if (res && res.status && res.data) {
        setTickets(res.data);
        if (res.data.length > 0 && !selectedTicketId) {
          setSelectedTicketId(res.data[0].id);
        }
      } else if (res && Array.isArray(res)) {
          setTickets(res);
          if (res.length > 0 && !selectedTicketId) {
              setSelectedTicketId(res[0].id);
          }
      }
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const selectedTicket = tickets.find(t => t.id === selectedTicketId) || (tickets.length > 0 ? tickets[0] : null);

  const handleSendReply = async (e, resolveTicket = false) => {
    if (e) e.preventDefault();
    if (!replyText.trim() || !selectedTicket) return;
    
    try {
      const senderName = 'W2D Staff';
      const res = await DataService.post('/support/reply', {
        ticketId: selectedTicket.id,
        sender: senderName,
        text: resolveTicket ? `[Ticket Resolved] ${replyText}` : replyText
      });

      if (res && (res.status || res.success)) {
        if (resolveTicket) {
          // Fire and forget status update, it will trigger fetchTickets internally
          await handleUpdateStatus('Resolved');
        } else {
          fetchTickets();
        }
        setReplyText('');
      }
    } catch (error) {
      console.error('Error adding reply:', error);
    }
  };

  const handleConfirmResolve = async () => {
    if (!resolveReason.trim() || !selectedTicket) return;
    
    try {
      const senderName = 'W2D Staff';
      const res = await DataService.post('/support/reply', {
        ticketId: selectedTicket.id,
        sender: senderName,
        text: `[Resolution Reason] ${resolveReason}`
      });

      if (res && (res.status || res.success)) {
        await handleUpdateStatus('Resolved');
        setShowResolveBox(false);
        setResolveReason('');
      }
    } catch (error) {
      console.error('Error confirming resolve:', error);
    }
  };

  const handleUpdateStatus = async (newStatus) => {
    if (!selectedTicket) return;
    try {
      const res = await DataService.put('/support/update', {
        ticketId: selectedTicket.id,
        status: newStatus
      });
      if (res && (res.status || res.success)) fetchTickets();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleAssignTicket = async (newStaff) => {
    if (!selectedTicket) return;
    try {
      const res = await DataService.put('/support/update', {
        ticketId: selectedTicket.id,
        assignedTo: newStaff
      });
      if (res && (res.status || res.success)) fetchTickets();
    } catch (error) {
      console.error('Error updating assignment:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Open': return { bg: '#fff7ed', text: '#ea580c', glow: '0 0 10px rgba(234, 88, 12, 0.3)' };
      case 'In Progress': return { bg: '#eff6ff', text: '#2563eb', glow: '0 0 10px rgba(37, 99, 235, 0.3)' };
      case 'Resolved': return { bg: '#f0fdf4', text: '#16a34a', glow: '0 0 10px rgba(22, 163, 74, 0.3)' };
      case 'Closed': return { bg: '#f1f5f9', text: '#64748b', glow: 'none' };
      case 'Waiting for Customer': return { bg: '#fef2f2', text: '#dc2626', glow: '0 0 10px rgba(220, 38, 38, 0.3)' };
      case 'Waiting for Retailer': return { bg: '#fdf4ff', text: '#c026d3', glow: '0 0 10px rgba(192, 38, 211, 0.3)' };
      default: return { bg: '#f1f5f9', text: '#64748b', glow: 'none' };
    }
  };

  const getStatusBadge = (status) => {
    const style = getStatusColor(status);
    return (
      <span style={{ 
        background: style.bg, 
        color: style.text, 
        padding: '3px 8px', 
        borderRadius: '6px', 
        fontSize: '0.65rem', 
        fontWeight: '800',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        boxShadow: style.glow
      }}>
        {status}
      </span>
    );
  };

  const filteredTickets = tickets.filter(t => {
    if (statusFilter === 'All') return true;
    return t.status === statusFilter;
  });

  return (
    <div className="container-fluid" style={{ marginTop: '20px' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '24px' }}>
        
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '900', color: 'var(--text-dark)', margin: 0 }}>Support Ticket Console</h2>
            <p style={{ fontSize: '0.75rem', color: '#64748b', margin: '4px 0 0 0' }}>Centralized inquiry and resolution pipeline for World2Door Cargo.</p>
          </div>
        </div>

        {/* Main Grid: Left List (1fr) / Right Details (2fr) */}
        <div style={{ display: 'grid', gridTemplateColumns: '1.1fr 2fr', gap: '24px', alignItems: 'stretch' }}>
          
          {/* Left Side: Ticket List Panel */}
          <div style={{ 
            background: '#ffffff', 
            borderRadius: '18px', 
            border: '1px solid rgba(226, 232, 240, 0.8)',
            boxShadow: '0 4px 15px rgba(0,0,0,0.01)',
            display: 'flex',
            flexDirection: 'column',
            height: '620px'
          }}>
            {/* Header Controls */}
            <div style={{ padding: '20px', borderBottom: '1px solid #f1f5f9' }}>
              
              {/* Status Filter */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FiFilter size={12} color="#94a3b8" />
                <select 
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  style={{
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    padding: '8px 8px',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    fontFamily: 'inherit',
                    color: '#64748b',
                    outline: 'none',
                    flex: 1
                  }}
                >
                  <option value="All">All Tickets</option>
                  <option value="Open">Open</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Waiting for Customer">Waiting for Customer</option>
                  <option value="Waiting for Retailer">Waiting for Retailer</option>
                  <option value="Resolved">Resolved</option>
                  <option value="Closed">Closed</option>
                </select>
              </div>
            </div>

            {/* Ticket list */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '10px' }}>
              {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '40px' }}>
                  <div style={{ width: '24px', height: '24px', border: '3px solid #e2e8f0', borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                  <style>
                    {`
                      @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                      }
                    `}
                  </style>
                </div>
              ) : filteredTickets.length === 0 ? (
                <p style={{ textAlign: 'center', padding: '20px', color: '#94a3b8' }}>No tickets found.</p>
              ) : (
                filteredTickets.map(t => (
                  <div 
                    key={t.id}
                    onClick={() => setSelectedTicketId(t.id)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(248, 250, 252, 0.8)';
                      e.currentTarget.style.transform = 'scale(1.01)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = selectedTicketId === t.id ? 'rgba(37, 99, 235, 0.04)' : 'transparent';
                      e.currentTarget.style.transform = 'scale(1)';
                    }}
                    style={{
                      padding: '16px',
                      borderRadius: '12px',
                      background: selectedTicketId === t.id ? 'rgba(37, 99, 235, 0.04)' : 'transparent',
                      border: selectedTicketId === t.id 
                        ? '1.5px solid rgba(37, 99, 235, 0.2)' 
                        : '1px solid transparent',
                      cursor: 'pointer',
                      marginBottom: '8px',
                      transition: 'all 0.2s'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span style={{ fontSize: '0.65rem', color: '#94a3b8', fontWeight: '700' }}>{t.id}</span>
                      {getStatusBadge(t.status)}
                    </div>
                    <h4 style={{ fontSize: '0.75rem', fontWeight: '800', color: 'var(--text-dark)', marginBottom: '6px', lineHeight: '1.4' }}>
                      {t.subject}
                    </h4>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.65rem', color: '#94a3b8' }}>
                      <span>{t.category} • {t.raisedBy || t.retailerName || 'Retailer'}</span>
                      <span>{t.created ? t.created.split(' ')[0] : ''}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Right Side: Ticket Detail & Message Thread */}
          <div style={{ 
            background: '#ffffff', 
            borderRadius: '18px', 
            border: '1px solid rgba(226, 232, 240, 0.8)',
            boxShadow: '0 4px 15px rgba(0,0,0,0.01)',
            display: 'flex',
            flexDirection: 'column',
            height: '620px',
            overflow: 'hidden'
          }}>
            {selectedTicket ? (
              <>
                {/* Detail Header Info */}
                <div style={{ padding: '24px', borderBottom: '1px solid #f1f5f9', backgroundColor: 'rgba(248, 250, 252, 0.5)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                    <div>
                      <span style={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: '800' }}>SUPPORT CHANNEL • {selectedTicket.id}</span>
                      <h3 style={{ fontSize: '1rem', fontWeight: '900', color: 'var(--text-dark)', marginTop: '4px', letterSpacing: '-0.3px' }}>
                        {selectedTicket.subject}
                      </h3>
                    </div>
                    
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      {selectedTicket.status !== 'Resolved' && selectedTicket.status !== 'Closed' && !showResolveBox && (
                        <button 
                          onClick={() => setShowResolveBox(true)}
                          style={{
                            background: '#16a34a',
                            color: '#ffffff',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '4px 12px',
                            fontSize: '0.7rem',
                            fontWeight: '700',
                            cursor: 'pointer',
                            boxShadow: '0 2px 5px rgba(22, 163, 74, 0.3)'
                          }}
                        >
                          Mark as Resolved
                        </button>
                      )}
                      {getStatusBadge(selectedTicket.status)}
                    </div>
                  </div>

                  {showResolveBox && (
                    <div style={{ background: '#f0fdf4', padding: '16px', borderRadius: '8px', border: '1px solid #bbf7d0', marginBottom: '20px' }}>
                      <h4 style={{ fontSize: '0.8rem', color: '#166534', marginBottom: '8px', fontWeight: '700' }}>Provide Resolution Reason</h4>
                      <textarea
                        value={resolveReason}
                        onChange={(e) => setResolveReason(e.target.value)}
                        placeholder="Please explain how this ticket was resolved..."
                        style={{
                          width: '100%',
                          minHeight: '60px',
                          padding: '10px',
                          borderRadius: '6px',
                          border: '1px solid #86efac',
                          fontSize: '0.75rem',
                          fontFamily: 'inherit',
                          outline: 'none',
                          marginBottom: '10px'
                        }}
                      />
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                        <button
                          onClick={() => setShowResolveBox(false)}
                          style={{ background: '#ffffff', color: '#475569', border: '1px solid #cbd5e1', padding: '6px 12px', borderRadius: '6px', fontSize: '0.7rem', fontWeight: '600', cursor: 'pointer' }}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleConfirmResolve}
                          disabled={!resolveReason.trim()}
                          style={{ background: resolveReason.trim() ? '#16a34a' : '#86efac', color: '#ffffff', border: 'none', padding: '6px 12px', borderRadius: '6px', fontSize: '0.7rem', fontWeight: '700', cursor: resolveReason.trim() ? 'pointer' : 'not-allowed' }}
                        >
                          Confirm & Resolve
                        </button>
                      </div>
                    </div>
                  )}

                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', fontSize: '0.7rem', color: '#64748b', marginTop: showResolveBox ? '0' : '10px' }}>
                    <div>
                      <span style={{ color: '#94a3b8', fontWeight: '600' }}>Raised By:</span> <span style={{ fontWeight: '700', color: 'var(--text-dark)' }}>{selectedTicket.raisedBy || selectedTicket.retailerName || 'Unknown Retailer'}</span>
                    </div>
                    <div>
                      <span style={{ color: '#94a3b8', fontWeight: '600' }}>Date:</span> <span style={{ fontWeight: '700', color: 'var(--text-dark)' }}>{selectedTicket.created || 'N/A'}</span>
                    </div>
                    <div>
                      <span style={{ color: '#94a3b8', fontWeight: '600' }}>Category:</span> <span style={{ fontWeight: '700', color: 'var(--text-dark)' }}>{selectedTicket.category}</span>
                    </div>
                    <div>
                      <span style={{ color: '#94a3b8', fontWeight: '600' }}>Priority:</span> <span style={{ fontWeight: '700', color: selectedTicket.priority === 'High' ? '#ef4444' : '#64748b' }}>{selectedTicket.priority}</span>
                    </div>
                  </div>

                  {/* Staff Controls Drawer inline if Staff view is active */}
                  {viewPerspective === 'staff' && (
                    <div style={{ 
                      marginTop: '15px', 
                      background: '#ffffff', 
                      border: '1.5px solid rgba(37, 99, 235, 0.2)', 
                      borderRadius: '10px', 
                      padding: '10px 16px',
                      display: 'flex',
                      gap: '15px',
                      alignItems: 'center'
                    }}>
                      <span style={{ fontSize: '0.65rem', fontWeight: '800', color: '#2563eb', textTransform: 'uppercase' }}>Staff Actions:</span>
                      
                      {/* Status Dropdown */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '0.65rem', color: '#64748b', fontWeight: '600' }}>Status:</span>
                        <select 
                          value={selectedTicket.status}
                          onChange={(e) => handleUpdateStatus(e.target.value)}
                          style={{ background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.65rem', padding: '2px 6px', fontWeight: '700', fontFamily: 'inherit' }}
                        >
                          <option value="Open">Open</option>
                          <option value="In Progress">In Progress</option>
                          <option value="Waiting for Customer">Waiting for Customer</option>
                          <option value="Waiting for Retailer">Waiting for Retailer</option>
                          <option value="Resolved">Resolved</option>
                          <option value="Closed">Closed</option>
                        </select>
                      </div>

                      {/* Assignment Dropdown */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '0.65rem', color: '#64748b', fontWeight: '600' }}>Assignee:</span>
                        <select 
                          value={selectedTicket.assignedTo}
                          onChange={(e) => handleAssignTicket(e.target.value)}
                          style={{ background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.65rem', padding: '2px 6px', fontWeight: '700', fontFamily: 'inherit' }}
                        >
                          <option value="Unassigned">Unassigned</option>
                          <option value="Ali Al-Mansoori (Ops)">Ali Al-Mansoori (Ops)</option>
                          <option value="Sarah Jenkins (Finance)">Sarah Jenkins (Finance)</option>
                          <option value="System Automated">System Automated</option>
                        </select>
                      </div>
                    </div>
                  )}
                </div>

                {/* Description Block */}
                <div style={{ padding: '16px 24px', borderBottom: '1px solid #f1f5f9', background: '#f8fafc', fontSize: '0.75rem', color: 'var(--text-dark)', lineHeight: '1.5' }}>
                  <span style={{ display: 'block', fontSize: '0.65rem', fontWeight: '800', color: '#94a3b8', marginBottom: '4px', textTransform: 'uppercase' }}>Issue Description</span>
                  {selectedTicket.description}
                  {selectedTicket.attachments && selectedTicket.attachments.length > 0 && (
                    <div style={{ marginTop: '10px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                      {selectedTicket.attachments.map((file, idx) => (
                        <a key={idx} href={`${process.env.REACT_APP_API_BASEURL || 'https://www.world2doorcargo.com/api'}/uploads/support/${file}`} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
                          <span style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '4px 10px', display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '0.65rem', color: '#64748b', fontWeight: '600', cursor: 'pointer' }}>
                            <FiFileText size={12} color="#2563eb" /> {file}
                          </span>
                        </a>
                      ))}
                    </div>
                  )}
                </div>

                {/* Messages list */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {selectedTicket.thread && selectedTicket.thread.map((msg, idx) => {
                    const isStaff = msg.sender.includes('Staff') || msg.sender === 'System';
                    return (
                      <div 
                        key={idx}
                        style={{
                          alignSelf: isStaff ? 'flex-end' : 'flex-start',
                          maxWidth: '80%',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: isStaff ? 'flex-end' : 'flex-start'
                        }}
                      >
                        <span style={{ fontSize: '0.6rem', color: '#94a3b8', fontWeight: '700', marginBottom: '4px' }}>
                          {msg.sender} • {msg.time}
                        </span>
                        <div style={{
                          background: isStaff ? '#2563eb' : '#f1f5f9',
                          color: isStaff ? '#ffffff' : '#334155',
                          borderRadius: isStaff ? '12px 0 12px 12px' : '0 12px 12px 12px',
                          padding: '10px 14px',
                          fontSize: '0.75rem',
                          lineHeight: '1.4'
                        }}>
                          {msg.text}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Chat input box or Closed Message */}
                {selectedTicket.status === 'Resolved' || selectedTicket.status === 'Closed' ? (
                  <div style={{
                    padding: '16px 24px', 
                    borderTop: '1px solid #f1f5f9', 
                    background: '#f8fafc',
                    textAlign: 'center',
                    color: '#64748b',
                    fontSize: '0.75rem',
                    fontWeight: '600'
                  }}>
                    <FiCheckCircle size={16} color="#16a34a" style={{ marginBottom: '-3px', marginRight: '6px' }} />
                    This ticket has been marked as {selectedTicket.status} and is closed for further replies.
                  </div>
                ) : (
                  <form 
                    onSubmit={(e) => handleSendReply(e, false)}
                    style={{ 
                      padding: '16px 24px', 
                      borderTop: '1px solid #f1f5f9', 
                      display: 'flex', 
                      gap: '12px', 
                      alignItems: 'center' 
                    }}
                  >
                    <input 
                      type="text"
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder="Type a reply or closing message..."
                      style={{
                        flex: 1,
                        background: '#f8fafc',
                        border: '1px solid #cbd5e1',
                        borderRadius: '10px',
                        padding: '10px 16px',
                        fontSize: '0.75rem',
                        fontFamily: 'inherit',
                        outline: 'none'
                      }}
                    />
                    <button 
                      type="button"
                      onClick={(e) => handleSendReply(e, false)}
                      style={{ 
                        background: '#2563eb', 
                        color: '#ffffff', 
                        border: 'none', 
                        borderRadius: '10px', 
                        padding: '10px 16px', 
                        cursor: 'pointer',
                        boxShadow: '0 4px 10px rgba(37, 99, 235, 0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        fontSize: '0.75rem',
                        fontWeight: '700'
                      }}
                    >
                      <FiSend size={14} /> Send
                    </button>

                    <button 
                      type="button"
                      onClick={(e) => handleSendReply(e, true)}
                      disabled={!replyText.trim()}
                      style={{ 
                        background: replyText.trim() ? '#16a34a' : '#94a3b8', 
                        color: '#ffffff', 
                        border: 'none', 
                        borderRadius: '10px', 
                        padding: '10px 16px', 
                        cursor: replyText.trim() ? 'pointer' : 'not-allowed',
                        boxShadow: replyText.trim() ? '0 4px 10px rgba(22, 163, 74, 0.2)' : 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        fontSize: '0.75rem',
                        fontWeight: '700',
                        transition: 'all 0.2s'
                      }}
                    >
                      Resolve & Send
                    </button>
                  </form>
                )}
              </>
            ) : (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
                Select a ticket to view details
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};
